import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Trash2, ExternalLink, TrendingDown } from 'lucide-react';
import ConsumerNavbar from '../../components/consumer/ConsumerNavbar';
import { consumerAPI } from '../../services/consumerAPI';
import { PremiumFeature, PremiumBadge } from '../../components/PremiumFeatureLock';
import { toast } from 'sonner';

export default function WatchlistPage() {
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount
  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    // Check if user is logged in
    const userStr = sessionStorage.getItem('consumer_user');
    if (!userStr) {
      toast.error('Please sign in to view your watchlist');
      navigate('/auth');
      return;
    }

    try {
      const userData = JSON.parse(userStr);
      setUser(userData);
      setIsPremium(userData.subscription_tier === 'premium' || userData.premium === true);
      await loadWatchlist();
    } catch (error) {
      console.error('Failed to parse user:', error);
      navigate('/auth');
    }
  };

  const loadWatchlist = async () => {
    try {
      const data = await consumerAPI.getWatchlist();
      setWatchlist(data.deals || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load watchlist:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/auth');
      }
      setLoading(false);
    }
  };

  const handleRemove = async (dealId) => {
    try {
      await consumerAPI.removeFromWatchlist(dealId);
      toast.success('Removed from watchlist');
      setWatchlist(watchlist.filter(d => d._id !== dealId));
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const getCIScoreColor = (score) => {
    if (score >= 800) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 600) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 300) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ConsumerNavbar />
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerNavbar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                My Watchlist
                {!isPremium && watchlist.length >= 8 && (
                  <PremiumBadge onClick={() => {}} />
                )}
              </h1>
              <p className="text-gray-600">
                {watchlist.length} {watchlist.length === 1 ? 'deal' : 'deals'} saved
                {!isPremium && ` (${10 - watchlist.length} slots remaining)`}
                {isPremium && ' • Unlimited ✨'}
              </p>
            </div>
            {!isPremium && watchlist.length >= 10 && (
              <button
                onClick={() => navigate('/auth?upgrade=premium')}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold flex items-center gap-2"
              >
                🔓 Unlock Unlimited Watchlist
              </button>
            )}
          </div>
        </div>

        {/* Premium upsell banner for free users approaching limit */}
        {!isPremium && watchlist.length >= 8 && watchlist.length < 10 && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-900">
                  You're using {watchlist.length} of 10 free watchlist slots
                </p>
                <p className="text-xs text-purple-700 mt-1">
                  Upgrade to Premium for unlimited watchlist + advanced price alerts
                </p>
              </div>
              <button
                onClick={() => navigate('/auth?upgrade=premium')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                Upgrade ($4.99/mo)
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => navigate('/deals')}
            className="px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
          >
            + Add More Deals
          </button>
          <button
            onClick={() => navigate('/preferences')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            ⚙️ Alert Preferences
          </button>
        </div>

        {/* Watchlist Grid */}
        {watchlist.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-md border border-gray-200">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Your watchlist is empty</h3>
            <p className="text-gray-600 mb-6">Start adding deals to track prices and get alerts</p>
            <button
              onClick={() => navigate('/deals')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Browse Deals
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {watchlist.map((deal) => (
              <div
                key={deal._id}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex gap-6">
                  {/* Image */}
                  <div 
                    onClick={() => navigate(`/deal/${deal._id}`)}
                    className="flex-shrink-0 w-32 h-32 bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
                  >
                    <img
                      src={deal.product?.image_url}
                      alt={deal.product?.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{deal.product?.category}</div>
                        <h3 
                          onClick={() => navigate(`/deal/${deal._id}`)}
                          className="text-lg font-semibold text-gray-900 hover:text-blue-600 cursor-pointer mb-1"
                        >
                          {deal.product?.title}
                        </h3>
                        <p className="text-sm text-gray-600">from {deal.merchant?.name}</p>
                      </div>
                      <button
                        onClick={() => handleRemove(deal._id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove from watchlist"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Price Info */}
                    <div className="flex items-baseline gap-3 mb-3">
                      <span className="text-3xl font-bold text-gray-900">
                        ${deal.product?.price?.toFixed(2) || '0.00'}
                      </span>
                      <span className="text-lg text-gray-400 line-through">
                        ${deal.product?.msrp?.toFixed(2) || '0.00'}
                      </span>
                      {deal.product?.msrp && deal.product?.price && (
                        <span className="text-lg font-semibold text-green-600">
                          Save ${(deal.product.msrp - deal.product.price).toFixed(2)}
                        </span>
                      )}
                    </div>

                    {/* CI Score and Tags */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className={`px-3 py-1 rounded-md text-sm font-bold border ${getCIScoreColor(deal.ci_deal_score)}`}>
                        CI Score: {deal.ci_deal_score}
                      </div>
                      {deal.tags && deal.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {tag.toUpperCase()}
                        </span>
                      ))}
                      {deal.end_date && (
                        <span className="text-xs text-gray-500">
                          Expires: {new Date(deal.end_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => navigate(`/deal/${deal._id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Deal
                      </button>
                      {deal.coupon_code && (
                        <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <span className="text-xs text-gray-600 mr-2">Code:</span>
                          <code className="text-sm font-bold text-blue-700 font-mono">{deal.coupon_code}</code>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
